import { Button } from '@edsn/pie-components';
import IconClose from '@iconify/icons-material-symbols/close';
import xor from 'lodash.xor';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConditionalWrapper } from '#pie/utils/ConditionalWrapper';
import type { mapToFilterOptions } from '#pie/utils/mapToFilterOptions';
import { cn } from '#pie/utils/TailwindUtils';
import { Accordion, AccordionItem } from '../accordion/Accordion';
import { CollapseStack } from '../collapse-stack/CollapseStack';
import { Icon } from '../icon/Icon';
import { InputCheckbox } from '../input-checkbox/InputCheckbox';
import { InputSearch } from '../input-search/InputSearch';
import { Drawer } from '../layout/drawer/Drawer';
import { Text } from '../text/Text';

export interface Filter<TFilter extends object> {
  id: keyof TFilter;
  label: string;
  options: ReturnType<typeof mapToFilterOptions>;
  values: string[];
}

export interface Props<TFilter extends object> extends ComponentProps<'div'> {
  filters: Filter<TFilter>[];
  activeFilters: TFilter;
  setActiveFilters: (activeFilters: TFilter) => TFilter;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drawer?: boolean;
}

export const DataFilters = <TFilter extends object>({
  filters,
  activeFilters,
  setActiveFilters,
  open = false,
  onOpenChange,
  drawer = false,
  className,
  ...props
}: Props<TFilter>) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState<Record<string, string>>({});

  /* istanbul ignore next -- @preserve */
  return (
    <ConditionalWrapper
      condition={drawer}
      // TODO: remove ignore when we start using the drawer functionality
      wrapper={(children) => (
        <Drawer
          title={t('common.filter.title')}
          aria-label={t('common.filter_panel.aria-label')}
          open={open}
          onOpenChange={onOpenChange}
          {...props}
        >
          {children}
        </Drawer>
      )}
      elseWrapper={(children) =>
        open ? (
          <div
            role="search"
            aria-label={t('common.filter_panel.aria-label')}
            className={cn('w-90.5', className)}
            {...props}
          >
            <div className="mb-4 flex items-center justify-between">
              <Text as="h2" variant="h4" className="text-content-heading">
                {t('common.filter.title')}
              </Text>
              <Button
                onClick={() => onOpenChange(!open)}
                aria-label={t('common.close')}
                type="button"
                action="tertiary"
                emphasis="minimal"
                className="p-0 hover:text-action-secondary-enabled"
                iconStart={<Icon icon={IconClose} />}
              />
            </div>
            {children}
          </div>
        ) : null
      }
    >
      <Accordion
        type="multiple"
        defaultValue={filters
          .filter((filter) => filter.values.length)
          .map(({ id }) => id as string)}
      >
        {filters.map(({ id, label, options, values }) => {
          /* istanbul ignore next -- @preserve */
          if (typeof id !== 'string')
            throw new Error('Filter id must be a string');

          return (
            <AccordionItem key={id} title={label} value={id}>
              {options.length > 15 && (
                <InputSearch
                  value={search[id] || ''}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [id]: e.target.value }))
                  }
                  placeholder={t('common.filter.search_placeholder')}
                  onClear={() => setSearch((prev) => ({ ...prev, [id]: '' }))}
                  fullWidth
                  className="mb-4"
                />
              )}
              <CollapseStack collapseAt={4}>
                {options
                  .filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(search[id]?.toLowerCase() ?? ''),
                  )
                  .map((option) => {
                    const optionId = `${id}-${option.value}`;
                    return (
                      <InputCheckbox
                        key={optionId}
                        id={optionId}
                        label={option.label}
                        value={option.value}
                        checked={values.includes(option.value)}
                        onCheckedChange={() => {
                          setActiveFilters({
                            ...activeFilters,
                            [id]: xor(
                              activeFilters[id as keyof TFilter] as string[],
                              [option.value],
                            ),
                          });
                        }}
                      />
                    );
                  })}
              </CollapseStack>
            </AccordionItem>
          );
        })}
      </Accordion>
    </ConditionalWrapper>
  );
};
